import React, { useState, useEffect, useMemo, useCallback } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import {
  FiPlus, FiUser, FiMail, FiPhone, FiTag, FiTrash2, FiEye,
  FiMapPin, FiBriefcase, FiX, FiLayout, FiSearch, FiKey, FiCamera,
  FiUsers, FiCalendar, FiChevronRight, FiCheckCircle, FiShield,
  FiActivity, FiGrid, FiRefreshCw, FiAlertCircle, FiList,
  FiFilter, FiChevronDown
} from "react-icons/fi";

/* ─────────────────────────────────────────
   CONFIG — unchanged
───────────────────────────────────────── */
const MEDIA_BASE = "https://api.ajplservices.tech/uploads/profile/";

const buildImageUrl = (profile_image) => {
  if (!profile_image) return null;
  const clean = profile_image.split("/").pop();
  return `${MEDIA_BASE}${clean}`;
};

const CAT_COLORS = [
  { bg: "#EEF2FF", color: "#422AFB", border: "#C7D2FE" },
  { bg: "#FEF3C7", color: "#B45309", border: "#FDE68A" },
  { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0" },
  { bg: "#FEE2E2", color: "#991B1B", border: "#FECACA" },
  { bg: "#F0FDF4", color: "#166534", border: "#BBF7D0" },
];
const getCatColor = (name = "") => CAT_COLORS[(name.charCodeAt(0) || 0) % CAT_COLORS.length];

/* ─────────────────────────────────────────
   AVATAR COMPONENT
───────────────────────────────────────── */
const Avatar = ({ src, name, size = 44, radius = "14px" }) => {
  const [err, setErr] = useState(false);
  const letter = (name || "?").charAt(0).toUpperCase();
  const hue = ((name || "A").charCodeAt(0) * 41) % 360;
  if (src && !err) {
    return (
      <img
        src={src} alt={name} onError={() => setErr(true)}
        style={{ width: size, height: size, borderRadius: radius, objectFit: "cover", flexShrink: 0, display: "block" }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      background: `linear-gradient(135deg, hsl(${hue},58%,52%), hsl(${hue+30},65%,44%))`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "#FFF", fontWeight: 900, fontSize: size * 0.38,
    }}>
      {letter}
    </div>
  );
};

/* ─────────────────────────────────────────
   TOAST
───────────────────────────────────────── */
const Toast = ({ type, text, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const ok = type === "success";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px", padding: "13px 18px",
      borderRadius: "14px", border: `1.5px solid ${ok ? "#A7F3D0" : "#FECACA"}`,
      background: ok ? "#ECFDF5" : "#FEF2F2", color: ok ? "#059669" : "#DC2626",
      boxShadow: "0 10px 30px rgba(0,0,0,0.12)", minWidth: "280px",
    }}>
      {ok ? <FiCheckCircle size={15}/> : <FiAlertCircle size={15}/>}
      <span style={{ fontWeight: 700, fontSize: "13px", flex: 1 }}>{text}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", display: "flex" }}><FiX size={13}/></button>
    </div>
  );
};

/* ─────────────────────────────────────────
   FORM FIELD COMPONENTS
───────────────────────────────────────── */
const FLabel = ({ children }) => (
  <label style={{ display: "block", fontSize: "10px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "7px" }}>
    {children}
  </label>
);

const FInput = ({ icon, type = "text", value, onChange, placeholder, required }) => (
  <div style={{ position: "relative" }}>
    {icon && <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8" }}>{icon}</span>}
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} required={required}
      style={{
        width: "100%", padding: icon ? "11px 14px 11px 38px" : "11px 14px",
        borderRadius: "11px", border: "1.5px solid #E2E8F0", outline: "none",
        background: "#FFF", fontSize: "13px", fontWeight: 600, color: "#1E293B",
        boxSizing: "border-box", transition: "border-color 0.2s", fontFamily: "inherit",
      }}
      onFocus={e => { e.target.style.borderColor = "#D4AF37"; }}
      onBlur={e => { e.target.style.borderColor = "#E2E8F0"; }}
    />
  </div>
);

const FSelect = ({ icon, value, onChange, children, required }) => (
  <div style={{ position: "relative" }}>
    {icon && <span style={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8", zIndex: 1 }}>{icon}</span>}
    <select
      value={value} onChange={e => onChange(e.target.value)} required={required}
      style={{
        width: "100%", padding: icon ? "11px 36px 11px 38px" : "11px 36px 11px 14px",
        borderRadius: "11px", border: "1.5px solid #E2E8F0", outline: "none",
        background: "#FFF", fontSize: "13px", fontWeight: 600, color: "#1E293B",
        appearance: "none", boxSizing: "border-box", cursor: "pointer", fontFamily: "inherit",
        transition: "border-color 0.2s",
      }}
      onFocus={e => { e.target.style.borderColor = "#D4AF37"; }}
      onBlur={e => { e.target.style.borderColor = "#E2E8F0"; }}
    >
      {children}
    </select>
    <FiChevronDown size={13} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8", pointerEvents: "none" }}/>
  </div>
);

const FormGroup = ({ label, children }) => (
  <div>
    <FLabel>{label}</FLabel>
    {children}
  </div>
);

const FormSection = ({ title, icon, children }) => (
  <div style={{ marginBottom: "22px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
      <div style={{ width: "28px", height: "28px", borderRadius: "9px", background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", color: "#D97706" }}>
        {icon}
      </div>
      <span style={{ fontSize: "12px", fontWeight: 800, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.8px" }}>{title}</span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {children}
    </div>
  </div>
);

const FormRow = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
    {children}
  </div>
);

/* ─────────────────────────────────────────
   STAT CARD
───────────────────────────────────────── */
const StatCard = ({ label, value, icon, iconBg, iconColor, isStatus }) => (
  <div style={S.statCard}>
    <div style={{ ...S.statIcon, background: iconBg, color: iconColor }}>{icon}</div>
    <div>
      <div style={S.statLabel}>{label}</div>
      <div style={{ ...S.statVal, color: isStatus ? "#059669" : "#0F172A" }}>
        {isStatus ? (
          <span style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#10B981", display: "inline-block", boxShadow: "0 0 0 3px #D1FAE5" }}/>
            {value}
          </span>
        ) : value}
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   DRAWER HELPERS
───────────────────────────────────────── */
const DrawerSection = ({ title, children }) => (
  <div style={{ marginBottom: "18px" }}>
    <div style={{ fontSize: "9px", fontWeight: 900, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "10px", paddingBottom: "6px", borderBottom: "1.5px solid #F1F5F9" }}>
      {title}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>{children}</div>
  </div>
);

const DrawerRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
    <div style={{ width: "32px", height: "32px", borderRadius: "10px", background: "#F8FAFC", border: "1.5px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", color: "#D4AF37", flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: "9px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
      <div style={{ fontSize: "13px", fontWeight: 700, color: "#1E293B", marginTop: "1px" }}>{value || "—"}</div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const Hods = () => {
  const [hods,       setHods]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal,  setShowModal]  = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [selectedHod,setSelectedHod]= useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode,   setViewMode]   = useState("table"); // "table" | "grid"
  const [submitting, setSubmitting] = useState(false);
  const [filterCat,  setFilterCat]  = useState("");
  const [toast,      setToast]      = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [formData, setFormData] = useState({
    username: "", email: "", phone: "", password: "", full_name: "",
    category_id: "", department: "", designation: "",
    floor_number: "", cabin_number: "", profile_image: null,
  });

  const showToast = useCallback((type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4200);
  }, []);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [hodRes, catRes] = await Promise.all([
        AdminService.getAllHods(),
        AdminService.getCategories(),
      ]);
      if (hodRes.success && Array.isArray(hodRes.data)) setHods(hodRes.data);
      else setHods([]);
      if (catRes.success && Array.isArray(catRes.data)) setCategories(catRes.data);
      else setCategories([]);
    } catch (err) {
      console.error("Load Error:", err);
      setHods([]); setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);

const handleCreateHOD = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  const data = new FormData();

  Object.keys(formData).forEach((key) => {
    if (formData[key] !== null && formData[key] !== "") {
      data.append(key, formData[key]);
    }
  });

  try {
    const res = await AdminService.createHod(data);

    if (res.success) {
      showToast("success", "Concern Person created successfully!");
      setShowModal(false);
      resetForm();
      fetchInitialData();
    } else {
      showToast("error", res.message || "Creation failed");
    }
  } catch (err) {
    console.log("Create HOD Error:", err);

    const msg =
      err?.message ||
      err?.response?.data?.message ||
      "Something went wrong";

    showToast("error", msg);
  } finally {
    setSubmitting(false);
  }
};

  const handleDelete = async (id) => {
    try {
      const res = await AdminService.deleteHod(id);
      if (res.success) {
        setHods(prev => prev.filter(h => h.id !== id));
        if (selectedHod?.id === id) setSelectedHod(null);
        setDeleteConfirm(null);
        showToast("success", "HOD removed successfully");
      } else {
        showToast("error", res.message || "Delete failed");
      }
    } catch {
      showToast("error", "Delete failed. Try again.");
    }
  };

  const resetForm = () => setFormData({
    username: "", email: "", phone: "", password: "", full_name: "",
    category_id: "", department: "", designation: "",
    floor_number: "", cabin_number: "", profile_image: null,
  });

  const filteredHods = useMemo(() => {
    return hods.filter(h => {
      const s = searchTerm.toLowerCase();
      const matchSearch =
        !searchTerm ||
        (h.full_name || h.username || "").toLowerCase().includes(s) ||
        (h.email || "").toLowerCase().includes(s) ||
        (h.category_name || "").toLowerCase().includes(s) ||
        (h.department || "").toLowerCase().includes(s);
      const matchCat = !filterCat || h.category_name === filterCat;
      return matchSearch && matchCat;
    });
  }, [hods, searchTerm, filterCat]);

  const catOptions = useMemo(() => [...new Set(hods.map(h => h.category_name).filter(Boolean))], [hods]);

  /* ── LOADER ── */
  if (loading) return (
    <div style={S.loaderPage}>
      <style>{CSS_ANIM}</style>
      <div style={S.spinner}/>
      <div style={S.loaderBrand}>ACHAL JEWELS</div>
      <div style={S.loaderSub}>Synchronizing Concern Person Registry...</div>
      <div style={{ display: "flex", gap: "6px", marginTop: "18px" }}>
        {[0,1,2].map(i => <div key={i} style={{ ...S.dot, animationDelay: `${i*0.2}s` }}/>)}
      </div>
    </div>
  );

  /* ══════════════ RENDER ══════════════ */
  return (
    <>
      <style>{CSS_ANIM + CSS_HOVER}</style>

      {/* Toast */}
      {toast && (
        <div style={S.toastWrap}>
          <Toast type={toast.type} text={toast.text} onClose={() => setToast(null)}/>
        </div>
      )}

      <div style={S.layout}>
        <AdminSidebar/>
        <div style={S.main}>

          {/* ══ HEADER ══ */}
          <div style={S.header}>
            <div>
              <div style={S.breadcrumb}>
                <span style={{ color: "#D4AF37", fontWeight: 800 }}>Admin</span>
                <FiChevronRight size={12} style={{ color: "#CBD5E1" }}/>
                <span>Concern Person Management</span>
              </div>
              <h1 style={S.title}>Concern Person <span style={{ color: "#D4AF37" }}>Command Center</span></h1>
              <p style={S.subtitle}>Manage department heads, category assignments and access control</p>
            </div>
            <div style={S.headerRight}>
              <button onClick={fetchInitialData} className="r-btn" style={S.refreshBtn}>
                <FiRefreshCw size={14}/> Refresh
              </button>
              <button onClick={() => setShowModal(true)} style={S.addBtn}>
                <FiPlus size={15}/> Create New 
              </button>
            </div>
          </div>

          {/* ══ STATS ══ */}
          <div style={S.statsRow}>
            <StatCard label="Total Concern Persons"      value={hods.length}        icon={<FiUsers size={18}/>}    iconBg="#EEF2FF" iconColor="#422AFB"/>
            <StatCard label="Active Categories" value={categories.length} icon={<FiTag size={18}/>}      iconBg="#FEF3C7" iconColor="#D97706"/>
            <StatCard label="Filtered"        value={filteredHods.length} icon={<FiSearch size={18}/>}  iconBg="#F8FAFC" iconColor="#475569"/>
          </div>

          {/* ══ CONTROLS ══ */}
          <div style={S.controlRow}>
            {/* Search */}
            <div style={S.searchBox}>
              <FiSearch style={S.searchIcon}/>
              <input
                placeholder="Search name, email, category..."
                style={S.searchInput}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} style={S.clearSearch}><FiX size={12}/></button>
              )}
            </div>

            {/* Category filter */}
            <div style={S.filterSelectWrap}>
              <FiFilter size={12} style={{ color: "#94A3B8", position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }}/>
              <select
                value={filterCat}
                onChange={e => setFilterCat(e.target.value)}
                style={S.filterSelect}
              >
                <option value="">All Categories</option>
                {catOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <FiChevronDown size={12} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8", pointerEvents: "none" }}/>
            </div>

            {/* View toggle */}
            <div style={S.viewToggle}>
              <button
                onClick={() => setViewMode("table")}
                style={{ ...S.toggleBtn, background: viewMode === "table" ? "#1E293B" : "transparent", color: viewMode === "table" ? "#FFF" : "#94A3B8" }}
              ><FiList size={14}/></button>
              <button
                onClick={() => setViewMode("grid")}
                style={{ ...S.toggleBtn, background: viewMode === "grid" ? "#1E293B" : "transparent", color: viewMode === "grid" ? "#FFF" : "#94A3B8" }}
              ><FiGrid size={14}/></button>
            </div>

            <div style={S.resultCount}>
              <b style={{ color: "#0F172A" }}>{filteredHods.length}</b>
              <span style={{ color: "#94A3B8" }}> / {hods.length}</span>
            </div>
          </div>

          {/* ══ MAIN CONTENT AREA ══ */}
          <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

            {/* ── TABLE VIEW ── */}
            {viewMode === "table" && (
              <div style={{ ...S.panel, flex: selectedHod ? "0 0 61%" : "1 1 100%", transition: "flex 0.3s ease" }}>
                {filteredHods.length === 0 ? (
                  <div style={S.emptyState}>
                    <div style={S.emptyIcon}><FiUsers size={32} style={{ color: "#CBD5E1" }}/></div>
                    <div style={S.emptyTitle}>{searchTerm || filterCat ? "No Concern Persons match your filters" : "No Concern Persons registered yet"}</div>
                    <div style={S.emptySub}>{searchTerm || filterCat ? "Try clearing your search or filter" : "Click 'Initialize Concern Person' to add the first department head"}</div>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          {["Concern Person Profile", "Department", "Category", "Location", "Joined", "Actions"].map(h => (
                            <th key={h} style={S.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHods.map((hod, idx) => {
                          const imgUrl = buildImageUrl(hod.profile_image);
                          const cat    = getCatColor(hod.category_name || "");
                          const isSel  = selectedHod?.id === hod.id;
                          return (
                            <tr
                              key={hod.id}
                              className="t-row"
                              style={{ background: isSel ? "#F8FAFF" : "#FFF", animation: `fadeUp 0.3s ease ${Math.min(idx * 0.04, 0.4)}s both` }}
                            >
                              <td style={S.td}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                  <div style={{ position: "relative" }}>
                                    <Avatar src={imgUrl} name={hod.full_name || hod.username} size={42}/>
                                    <div style={S.onlineDot}/>
                                  </div>
                                  <div>
                                    <div style={S.hodName}>{hod.full_name || hod.username}</div>
                                    <div style={S.hodEmail}>{hod.email}</div>
                                    <div style={S.hodPhone}><FiPhone size={9}/> {hod.phone}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={S.td}>
                                <div style={S.deptName}>{hod.department || "Corporate"}</div>
                                <div style={S.deptDesig}><FiShield size={10}/> {hod.designation || "Head of Department"}</div>
                              </td>
                              <td style={S.td}>
                                {hod.category_name
                                  ? <span style={{ ...S.catPill, background: cat.bg, color: cat.color, border: `1.5px solid ${cat.border}` }}>{hod.category_name}</span>
                                  : <span style={S.unassigned}>Unassigned</span>}
                              </td>
                              <td style={S.td}>
                                {hod.floor_number || hod.cabin_number
                                  ? <div style={S.locCell}><FiMapPin size={11} style={{ color: "#D4AF37" }}/> Floor {hod.floor_number || "—"} · Cabin {hod.cabin_number || "—"}</div>
                                  : <span style={{ color: "#CBD5E1", fontSize: "13px" }}>—</span>}
                              </td>
                              <td style={S.td}>
                                <div style={S.dateText}>
                                  {hod.created_at ? new Date(hod.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                                </div>
                              </td>
                              <td style={S.td}>
                                <div style={{ display: "flex", gap: "7px", justifyContent: "flex-end" }}>
                                  <button
                                    onClick={() => setSelectedHod(isSel ? null : hod)}
                                    className="view-btn"
                                    style={{ ...S.viewBtn, background: isSel ? "#EEF2FF" : "#F8FAFC", color: isSel ? "#422AFB" : "#475569", borderColor: isSel ? "#C7D2FE" : "#E2E8F0" }}
                                  >
                                    <FiEye size={13}/> View
                                  </button>
                                  <button onClick={() => setDeleteConfirm(hod)} className="del-btn" style={S.delBtn}>
                                    <FiTrash2 size={13}/>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── GRID VIEW ── */}
            {viewMode === "grid" && (
              <div style={{ flex: 1, minWidth: 0 }}>
                {filteredHods.length === 0 ? (
                  <div style={{ ...S.emptyState, background: "#FFF", borderRadius: "20px", border: "1.5px solid #F1F5F9" }}>
                    <div style={S.emptyIcon}><FiUsers size={32} style={{ color: "#CBD5E1" }}/></div>
                    <div style={S.emptyTitle}>{searchTerm || filterCat ? "No Concern Persons match your filters" : "No Concern Persons registered yet"}</div>
                    <div style={S.emptySub}>{searchTerm || filterCat ? "Clear filters to see all Concern Persons" : "Click 'Initialize Concern Person' to get started"}</div>
                  </div>
                ) : (
                  <div style={S.gridWrap}>
                    {filteredHods.map((hod, idx) => {
                      const imgUrl = buildImageUrl(hod.profile_image);
                      const cat    = getCatColor(hod.category_name || "");
                      return (
                        <div
                          key={hod.id}
                          className="g-hod-card"
                          style={{ ...S.gridCard, animationDelay: `${Math.min(idx * 0.05, 0.5)}s` }}
                        >
                          {/* Gold top strip */}
                          <div style={{ height: "4px", background: "linear-gradient(90deg,#D4AF37,#F0CC5F,#D4AF37)", margin: "-20px -20px 16px -20px", borderRadius: "20px 20px 0 0" }}/>

                          <div style={{ display: "flex", alignItems: "flex-start", gap: "13px", marginBottom: "14px" }}>
                            <div style={{ position: "relative" }}>
                              <Avatar src={imgUrl} name={hod.full_name || hod.username} size={52} radius="15px"/>
                              <div style={{ ...S.onlineDot, width: "11px", height: "11px", bottom: "-1px", right: "-1px" }}/>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: "15px", color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {hod.full_name || hod.username}
                              </div>
                              <div style={{ fontSize: "11px", color: "#94A3B8", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hod.email}</div>
                              <div style={{ fontSize: "11px", color: "#94A3B8", display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" }}><FiPhone size={10}/>{hod.phone}</div>
                            </div>
                          </div>

                          {hod.category_name && (
                            <span style={{ ...S.catPill, background: cat.bg, color: cat.color, border: `1.5px solid ${cat.border}`, marginBottom: "10px", display: "inline-block" }}>
                              {hod.category_name}
                            </span>
                          )}

                          <div style={{ fontSize: "12px", color: "#475569", fontWeight: 700, marginBottom: "4px" }}>{hod.department || "Corporate"}</div>
                          <div style={{ fontSize: "11px", color: "#94A3B8", display: "flex", alignItems: "center", gap: "5px" }}>
                            <FiShield size={10}/> {hod.designation || "Head of Department"}
                          </div>

                          {(hod.floor_number || hod.cabin_number) && (
                            <div style={{ ...S.locCell, marginTop: "8px", fontSize: "11px" }}>
                              <FiMapPin size={10} style={{ color: "#D4AF37" }}/> Floor {hod.floor_number || "—"} · Cabin {hod.cabin_number || "—"}
                            </div>
                          )}

                          <div style={{ display: "flex", gap: "8px", marginTop: "16px", paddingTop: "14px", borderTop: "1.5px solid #F8FAFC" }}>
                            <button
                              onClick={() => { setSelectedHod(hod); setViewMode("table"); }}
                              className="view-btn"
                              style={{ ...S.viewBtn, flex: 1, justifyContent: "center" }}
                            >
                              <FiEye size={13}/> View
                            </button>
                            <button onClick={() => setDeleteConfirm(hod)} className="del-btn" style={S.delBtn}>
                              <FiTrash2 size={13}/>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── PROFILE DRAWER ── */}
            {selectedHod && viewMode === "table" && (
              <div style={S.drawer}>
                {/* Banner */}
                <div style={S.drawerBanner}>
                  <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 80% 20%, rgba(212,175,55,0.18) 0%, transparent 55%)", pointerEvents: "none" }}/>
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ fontSize: "9px", fontWeight: 900, color: "rgba(255,255,255,0.45)", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "4px" }}>Concern Person PROFILE</div>
                  </div>
                  <button onClick={() => setSelectedHod(null)} style={S.drawerCloseBtn}><FiX size={15}/></button>
                </div>

                {/* Avatar floating */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "-36px", position: "relative", zIndex: 2, marginBottom: "6px" }}>
                  <div style={{ padding: "4px", background: "#FFF", borderRadius: "22px", boxShadow: "0 6px 24px rgba(0,0,0,0.12)" }}>
                    <Avatar src={buildImageUrl(selectedHod.profile_image)} name={selectedHod.full_name || selectedHod.username} size={76} radius="18px"/>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#10B981", fontWeight: 700, marginTop: "8px" }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#10B981", display: "inline-block" }}/>Active
                  </div>
                </div>

                <div style={S.drawerBody}>
                  {/* Name + role */}
                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    <div style={{ fontSize: "18px", fontWeight: 900, color: "#0F172A" }}>{selectedHod.full_name || selectedHod.username}</div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "#64748B", fontWeight: 600, background: "#F8FAFC", padding: "5px 12px", borderRadius: "30px", marginTop: "6px", border: "1.5px solid #F1F5F9" }}>
                      <FiShield size={11} style={{ color: "#D4AF37" }}/> {selectedHod.designation || "Head of Department"}
                    </div>
                    {selectedHod.category_name && (
                      <div style={{ marginTop: "8px" }}>
                        <span style={{ ...S.catPill, ...getCatColor(selectedHod.category_name), border: `1.5px solid ${getCatColor(selectedHod.category_name).border}` }}>
                          {selectedHod.category_name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Contact chips */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "18px" }}>
                    <a href={`mailto:${selectedHod.email}`} style={S.contactChip}>
                      <FiMail size={13} style={{ color: "#422AFB" }}/> <span style={{ fontSize: "12px", fontWeight: 600, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedHod.email}</span>
                    </a>
                    <a href={`tel:${selectedHod.phone}`} style={{ ...S.contactChip, background: "#ECFDF5", borderColor: "#D1FAE5" }}>
                      <FiPhone size={13} style={{ color: "#059669" }}/> <span style={{ fontSize: "12px", fontWeight: 600, color: "#065F46" }}>{selectedHod.phone}</span>
                    </a>
                  </div>

                  <DrawerSection title="Professional Details">
                    <DrawerRow icon={<FiBriefcase size={13}/>} label="Department"  value={selectedHod.department}/>
                    <DrawerRow icon={<FiShield size={13}/>}    label="Designation" value={selectedHod.designation}/>
                    <DrawerRow icon={<FiUser size={13}/>}      label="Username"    value={selectedHod.username}/>
                  </DrawerSection>

                  <DrawerSection title="Office Location">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      {[
                        { icon: <FiMapPin size={14}/>, label: "Floor", val: selectedHod.floor_number },
                        { icon: <FiLayout size={14}/>, label: "Cabin", val: selectedHod.cabin_number },
                      ].map(({ icon, label, val }) => (
                        <div key={label} style={{ background: "#F8FAFC", borderRadius: "12px", padding: "12px", textAlign: "center", border: "1.5px solid #F1F5F9" }}>
                          <div style={{ color: "#D4AF37", marginBottom: "4px" }}>{icon}</div>
                          <div style={{ fontSize: "9px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
                          <div style={{ fontSize: "16px", fontWeight: 900, color: "#0F172A", marginTop: "2px" }}>{val || "N/A"}</div>
                        </div>
                      ))}
                    </div>
                  </DrawerSection>

                  {/* Joined */}
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "10px", color: "#94A3B8", fontWeight: 600, justifyContent: "center", paddingTop: "12px", borderTop: "1.5px solid #F1F5F9" }}>
                    <FiCalendar size={11}/>
                    Registered {selectedHod.created_at ? new Date(selectedHod.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
                  </div>

                  {/* Danger */}
                  <button
                    onClick={() => setDeleteConfirm(selectedHod)}
                    className="danger-btn"
                    style={S.drawerDangerBtn}
                  >
                    <FiTrash2 size={13}/> Remove Access
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ══════════════ CREATE HOD MODAL ══════════════ */}
      {showModal && (
        <div style={S.modalOverlay} onClick={() => { setShowModal(false); resetForm(); }}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={S.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{ width: "48px", height: "48px", borderRadius: "15px", background: "linear-gradient(135deg,#D4AF37,#F0CC5F)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(212,175,55,0.35)" }}>
                  <FiUser size={20} color="#0F172A"/>
                </div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: 900, color: "#0F172A", margin: 0 }}>Initialize Concern Person's Account</div>
                  <div style={{ fontSize: "13px", color: "#94A3B8", fontWeight: 500, marginTop: "3px" }}>Setup a new department head with full access credentials</div>
                </div>
              </div>
              <button onClick={() => { setShowModal(false); resetForm(); }} style={S.modalCloseBtn}><FiX size={18}/></button>
            </div>

            {/* Gold line */}
            <div style={{ height: "3px", background: "linear-gradient(90deg,#D4AF37,#F0CC5F,#D4AF37)" }}/>

            {/* Modal Body — scrollable */}
            <form onSubmit={handleCreateHOD} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
              <div style={S.modalScroll}>

                {/* Section 1: Personal */}
                <FormSection title="Personal Credentials" icon={<FiUser size={14}/>}>
                  <FormRow>
                    <FormGroup label="Full Name *">
                      <FInput icon={<FiUser size={13}/>} value={formData.full_name} onChange={v => setFormData(p => ({...p, full_name: v}))} placeholder="e.g. Gaurav Sharma" required/>
                    </FormGroup>
                    <FormGroup label="Username *">
                      <FInput icon={<FiShield size={13}/>} value={formData.username} onChange={v => setFormData(p => ({...p, username: v}))} placeholder="e.g. gaurav_hod" required/>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup label="Email Address *">
                      <FInput icon={<FiMail size={13}/>} type="email" value={formData.email} onChange={v => setFormData(p => ({...p, email: v}))} placeholder="gaurav@achal.com" required/>
                    </FormGroup>
                    <FormGroup label="Access Password *">
                      <FInput icon={<FiKey size={13}/>} type="password" value={formData.password} onChange={v => setFormData(p => ({...p, password: v}))} placeholder="Min. 8 characters" required/>
                    </FormGroup>
                  </FormRow>
                  <FormRow>
                    <FormGroup label="Phone Number *">
                      <FInput icon={<FiPhone size={13}/>} value={formData.phone} onChange={v => setFormData(p => ({...p, phone: v}))} placeholder="9876543210" required/>
                    </FormGroup>
                    <FormGroup label="Assigned Category *">
                      <FSelect icon={<FiTag size={13}/>} value={formData.category_id} onChange={v => setFormData(p => ({...p, category_id: v}))} required>
                        <option value="">Select Department Category</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </FSelect>
                    </FormGroup>
                  </FormRow>
                </FormSection>

                {/* Divider */}
                <div style={{ height: "1.5px", background: "#F1F5F9", margin: "4px 0 22px" }}/>

                {/* Section 2: Professional */}
                <FormSection title="Professional Assignment" icon={<FiBriefcase size={14}/>}>
                  <FormRow>
                    <FormGroup label="Department Name">
                      <FInput icon={<FiBriefcase size={13}/>} value={formData.department} onChange={v => setFormData(p => ({...p, department: v}))} placeholder="e.g. Maintenance"/>
                    </FormGroup>
                    <FormGroup label="Designation">
                      <FInput icon={<FiShield size={13}/>} value={formData.designation} onChange={v => setFormData(p => ({...p, designation: v}))} placeholder="e.g. Senior Manager"/>
                    </FormGroup>
                  </FormRow>
                </FormSection>

                {/* Divider */}
                <div style={{ height: "1.5px", background: "#F1F5F9", margin: "4px 0 22px" }}/>

                {/* Section 3: Location */}
                <FormSection title="Workspace Location" icon={<FiMapPin size={14}/>}>
                  <FormRow>
                    <FormGroup label="Floor Number">
                      <FInput icon={<FiMapPin size={13}/>} value={formData.floor_number} onChange={v => setFormData(p => ({...p, floor_number: v}))} placeholder="e.g. 2nd Floor"/>
                    </FormGroup>
                    <FormGroup label="Cabin / Room No.">
                      <FInput icon={<FiLayout size={13}/>} value={formData.cabin_number} onChange={v => setFormData(p => ({...p, cabin_number: v}))} placeholder="e.g. C-104"/>
                    </FormGroup>
                  </FormRow>
                </FormSection>

                {/* Divider */}
                <div style={{ height: "1.5px", background: "#F1F5F9", margin: "4px 0 22px" }}/>

                {/* Section 4: Photo */}
                <FormSection title="Profile Photo" icon={<FiCamera size={14}/>}>
                  <label htmlFor="hod-photo" style={S.uploadZone}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                      <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FiCamera size={20} style={{ color: "#94A3B8" }}/>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        {formData.profile_image ? (
                          <><span style={{ color: "#059669", fontWeight: 800 }}>✓ </span><span style={{ fontWeight: 700, color: "#1E293B" }}>{formData.profile_image.name}</span></>
                        ) : (
                          <><span style={{ fontWeight: 800, color: "#1E293B" }}>Click to upload photo</span><br/>
                          <span style={{ fontSize: "12px", color: "#94A3B8" }}>JPG or PNG, max 5MB</span></>
                        )}
                      </div>
                    </div>
                    <input
                      type="file" id="hod-photo" accept="image/jpeg,image/png,image/jpg"
                      onChange={e => setFormData(p => ({...p, profile_image: e.target.files[0]}))}
                      style={{ display: "none" }}
                    />
                  </label>
                </FormSection>

              </div>

              {/* Modal Footer */}
              <div style={S.modalFooter}>
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} style={S.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={{ ...S.submitBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
                  {submitting
                    ? <><div style={S.miniSpinner}/> Creating...</>
                    : <><FiCheckCircle size={14}/> Create  Account</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════ DELETE CONFIRM ══════════════ */}
      {deleteConfirm && (
        <div style={S.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...S.modal, maxWidth: "420px", flex: "none" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "center", padding: "28px 0 16px" }}>
              <div style={{ width: "60px", height: "60px", borderRadius: "18px", background: "linear-gradient(135deg,#EF4444,#DC2626)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(220,38,38,0.3)" }}>
                <FiTrash2 size={24} color="#FFF"/>
              </div>
            </div>
            <div style={{ textAlign: "center", padding: "0 32px 28px" }}>
              <div style={{ fontSize: "20px", fontWeight: 900, color: "#0F172A", marginBottom: "10px" }}>Remove Access?</div>
              <div style={{ fontSize: "13px", color: "#64748B", lineHeight: "1.7", marginBottom: "24px" }}>
                This will permanently remove <strong>{deleteConfirm.full_name || deleteConfirm.username}</strong>'s Concern Person account and all category mappings. This cannot be undone.
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <button onClick={() => setDeleteConfirm(null)} style={{ ...S.cancelBtn, flex: 1 }}>Cancel</button>
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#FFF", fontWeight: 800, cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", boxShadow: "0 4px 16px rgba(220,38,38,0.3)" }}
                >
                  <FiTrash2 size={13}/> Confirm Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────
   CSS
───────────────────────────────────────── */
const CSS_ANIM = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@500;600&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
  @keyframes pulse   { 0%,100%{ opacity:1; } 50%{ opacity:0.3; } }
  @keyframes popIn   { from { opacity:0; transform:scale(0.94) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
  @keyframes slideDown { from { opacity:0; transform:translateY(-14px); } to { opacity:1; transform:translateY(0); } }
`;
const CSS_HOVER = `
  .t-row:hover td      { background: #FAFBFF !important; }
  .t-row .view-btn, .t-row .del-btn { transition: all 0.18s; }
  .view-btn:hover { background: #EEF2FF !important; color: #422AFB !important; border-color: #C7D2FE !important; }
  .del-btn:hover  { background: #DC2626 !important; color: #FFF !important; border-color: #DC2626 !important; }
  .g-hod-card { transition: transform 0.22s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.22s; }
  .g-hod-card:hover { transform: translateY(-5px); box-shadow: 0 24px 48px rgba(0,0,0,0.1) !important; }
  .r-btn { transition: all 0.2s; }
  .r-btn:hover { background: #D4AF37 !important; color: #000 !important; border-color: #D4AF37 !important; }
  .danger-btn:hover { background: #DC2626 !important; color: #FFF !important; border-color: #DC2626 !important; }
  input:focus { border-color: #D4AF37 !important; box-shadow: 0 0 0 3px rgba(212,175,55,0.12) !important; }
  select:focus { border-color: #D4AF37 !important; box-shadow: 0 0 0 3px rgba(212,175,55,0.12) !important; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 99px; }
`;

/* ─────────────────────────────────────────
   STYLES
───────────────────────────────────────── */
const S = {
  layout: { display: "flex", background: "#F0F2F5", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" },
  main:   { marginLeft: "260px", padding: "32px 40px", flex: 1, minWidth: 0, overflowX: "hidden" },

  header:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" },
  breadcrumb: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#94A3B8", fontWeight: 600, marginBottom: "8px" },
  title:      { margin: 0, fontSize: "28px", fontWeight: 900, color: "#0F172A", letterSpacing: "-0.7px" },
  subtitle:   { margin: "4px 0 0", fontSize: "13px", color: "#94A3B8", fontWeight: 500 },
  headerRight:{ display: "flex", gap: "12px", alignItems: "center" },
  refreshBtn: { display: "flex", alignItems: "center", gap: "7px", padding: "11px 18px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#FFF", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569" },
  addBtn:     { display: "flex", alignItems: "center", gap: "7px", padding: "12px 22px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#D4AF37,#F0CC5F)", color: "#0F172A", fontWeight: 800, cursor: "pointer", fontSize: "13px", boxShadow: "0 4px 16px rgba(212,175,55,0.35)", transition: "all 0.2s" },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "14px", marginBottom: "22px" },
  statCard: { background: "#FFF", borderRadius: "16px", padding: "16px 18px", border: "1.5px solid #F1F5F9", display: "flex", alignItems: "center", gap: "14px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" },
  statIcon: { width: "44px", height: "44px", borderRadius: "13px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statLabel:{ display: "block", fontSize: "9px", fontWeight: 800, color: "#94A3B8", letterSpacing: "0.8px", textTransform: "uppercase" },
  statVal:  { fontSize: "24px", fontWeight: 900, marginTop: "4px" },

  controlRow:    { display: "flex", gap: "10px", alignItems: "center", marginBottom: "18px", flexWrap: "wrap" },
  searchBox:     { position: "relative", flex: 1, minWidth: "220px", maxWidth: "400px" },
  searchIcon:    { position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8" },
  searchInput:   { width: "100%", padding: "11px 36px 11px 40px", borderRadius: "12px", border: "1.5px solid #E2E8F0", outline: "none", background: "#FFF", fontSize: "13px", fontWeight: 500, color: "#1E293B", boxSizing: "border-box", transition: "border-color 0.2s" },
  clearSearch:   { position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "#F1F5F9", border: "none", borderRadius: "50%", width: "22px", height: "22px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" },
  filterSelectWrap: { position: "relative", minWidth: "180px" },
  filterSelect:  { width: "100%", padding: "11px 34px 11px 34px", borderRadius: "12px", border: "1.5px solid #E2E8F0", outline: "none", background: "#FFF", fontSize: "13px", fontWeight: 600, color: "#1E293B", appearance: "none", cursor: "pointer" },
  viewToggle:    { display: "flex", background: "#E8ECF0", borderRadius: "10px", padding: "3px", gap: "2px" },
  toggleBtn:     { border: "none", padding: "8px 12px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", transition: "all 0.2s" },
  resultCount:   { fontSize: "13px", marginLeft: "auto", whiteSpace: "nowrap" },

  /* Table panel */
  panel: { background: "#FFF", borderRadius: "22px", border: "1.5px solid #F1F5F9", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: "0 2px", minWidth: "800px" },
  th:    { padding: "13px 18px", textAlign: "left", fontSize: "10px", color: "#94A3B8", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 800, background: "#FFF", whiteSpace: "nowrap" },
  td:    { padding: "15px 18px", verticalAlign: "middle", borderBottom: "1.5px solid #F8FAFC", transition: "background 0.15s" },

  onlineDot:  { width: "9px", height: "9px", background: "#10B981", border: "2px solid #FFF", borderRadius: "50%", position: "absolute", bottom: -1, right: -1 },
  hodName:    { fontWeight: 800, color: "#0F172A", fontSize: "14px" },
  hodEmail:   { fontSize: "11px", color: "#94A3B8", marginTop: "2px" },
  hodPhone:   { fontSize: "10px", color: "#CBD5E1", fontWeight: 600, marginTop: "2px", display: "flex", alignItems: "center", gap: "4px" },
  deptName:   { fontWeight: 700, color: "#334155", fontSize: "13px" },
  deptDesig:  { fontSize: "11px", color: "#94A3B8", fontWeight: 600, marginTop: "3px", display: "flex", alignItems: "center", gap: "5px" },
  catPill:    { display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: "30px", fontSize: "11px", fontWeight: 800, whiteSpace: "nowrap" },
  unassigned: { display: "inline-flex", padding: "5px 12px", borderRadius: "30px", fontSize: "11px", fontWeight: 700, background: "#F8FAFC", color: "#CBD5E1", border: "1.5px dashed #E2E8F0" },
  locCell:    { display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: "#475569", fontWeight: 600 },
  dateText:   { fontSize: "12px", color: "#64748B", fontWeight: 600, whiteSpace: "nowrap" },
  viewBtn:    { display: "inline-flex", alignItems: "center", gap: "5px", padding: "7px 13px", borderRadius: "10px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", color: "#475569", fontSize: "12px", fontWeight: 700, cursor: "pointer", transition: "all 0.18s" },
  delBtn:     { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "34px", height: "34px", borderRadius: "10px", border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#EF4444", cursor: "pointer", transition: "all 0.18s" },

  /* Grid */
  gridWrap: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "16px" },
  gridCard: { background: "#FFF", borderRadius: "20px", padding: "20px", border: "1.5px solid #F1F5F9", boxShadow: "0 4px 16px rgba(0,0,0,0.05)", animation: "fadeUp 0.3s ease both" },

  /* Drawer */
  drawer:        { width: "300px", flexShrink: 0, background: "#FFF", borderRadius: "22px", border: "1.5px solid #F1F5F9", overflow: "hidden", position: "sticky", top: "20px", boxShadow: "0 8px 32px rgba(0,0,0,0.08)", animation: "slideIn 0.25s ease" },
  drawerBanner:  { height: "90px", background: "linear-gradient(135deg,#0F172A 0%,#1E293B 60%,#334155 100%)", position: "relative", display: "flex", alignItems: "flex-end", padding: "0 18px 12px" },
  drawerCloseBtn:{ position: "absolute", top: "12px", right: "12px", width: "30px", height: "30px", borderRadius: "50%", background: "rgba(255,255,255,0.14)", border: "none", color: "#FFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  drawerBody:    { padding: "12px 18px 22px" },
  contactChip:   { display: "flex", alignItems: "center", gap: "8px", padding: "9px 12px", borderRadius: "12px", background: "#EEF2FF", border: "1.5px solid #E0E7FF", textDecoration: "none", overflow: "hidden" },
  drawerDangerBtn:{ width: "100%", marginTop: "14px", padding: "11px", borderRadius: "12px", border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#EF4444", fontWeight: 700, fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px", fontFamily: "inherit", transition: "all 0.2s" },

  /* Empty */
  emptyState: { padding: "60px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" },
  emptyIcon:  { width: "72px", height: "72px", borderRadius: "22px", background: "#F8FAFC", border: "1.5px solid #F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "4px" },
  emptyTitle: { fontSize: "16px", fontWeight: 800, color: "#1E293B" },
  emptySub:   { fontSize: "13px", color: "#94A3B8", maxWidth: "320px" },

  /* Modal */
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(8,14,30,0.48)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px" },
  modal:        { background: "#FFF", width: "min(780px, 100%)", borderRadius: "24px", boxShadow: "0 24px 60px rgba(0,0,0,0.2)", maxHeight: "92vh", display: "flex", flexDirection: "column", animation: "popIn 0.26s ease", overflow: "hidden" },
  modalHeader:  { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "24px 28px", flexShrink: 0 },
  modalCloseBtn:{ width: "38px", height: "38px", borderRadius: "50%", background: "#F1F5F9", border: "none", color: "#475569", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  modalScroll:  { flex: 1, overflowY: "auto", padding: "24px 28px" },
  modalFooter:  { padding: "16px 28px 22px", borderTop: "1.5px solid #F1F5F9", display: "flex", justifyContent: "flex-end", gap: "12px", flexShrink: 0 },
  cancelBtn:    { padding: "12px 22px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569", fontFamily: "inherit" },
  submitBtn:    { padding: "12px 24px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#D4AF37,#F0CC5F)", color: "#0F172A", fontWeight: 800, cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 6px 20px rgba(212,175,55,0.35)", fontFamily: "inherit", transition: "opacity 0.2s" },
  miniSpinner:  { width: "14px", height: "14px", border: "2.5px solid rgba(0,0,0,0.2)", borderTopColor: "#0F172A", borderRadius: "50%", animation: "spin 0.7s linear infinite" },
  uploadZone:   { display: "flex", justifyContent: "center", borderRadius: "14px", border: "2px dashed #E2E8F0", background: "#F8FAFC", padding: "28px 20px", cursor: "pointer", transition: "border-color 0.2s" },

  /* Toast */
  toastWrap: { position: "fixed", top: "24px", right: "24px", zIndex: 9999, animation: "slideDown 0.3s ease" },

  /* Loader */
  loaderPage:  { height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", background: "#F0F2F5", fontFamily: "'DM Sans', sans-serif" },
  spinner:     { width: "44px", height: "44px", border: "5px solid #E2E8F0", borderTopColor: "#D4AF37", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loaderBrand: { marginTop: "20px", fontSize: "16px", fontWeight: 900, color: "#1E293B", letterSpacing: "4px" },
  loaderSub:   { color: "#94A3B8", fontSize: "12px", marginTop: "5px" },
  dot:         { width: "7px", height: "7px", borderRadius: "50%", background: "#D4AF37", animation: "pulse 1.2s ease infinite" },
};

export default Hods;